import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import "./dashboardStats.css";
import useApi from "../../helpers/apiHelper/requestHelper";
import { useDashboardMarket } from "../sharedPages/CommonComponents/DashboardMarketSelect/DashboardMarketContext";

const formatNumber = (value, decimals = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return (0).toFixed(decimals);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const getToneClass = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "neutral";
  return num > 0 ? "positive" : "negative";
};

const CopyTradingStats = ({ stats = {} }) => {
  const api = useApi();
  const { user } = useSelector((state) => state.user);
  const { selectedMarket } = useDashboardMarket();
  const [spotTotalTrades, setSpotTotalTrades] = useState(0);

  const isSpotMarket = selectedMarket === "spot";
  // Backend now returns pnl/dailyAvgProfit/avgTradeRoi inside both
  // stats.futures and stats.spot — read the market-scoped value directly
  // instead of the old "no spot data exists" 0 stand-in.
  const avgProfit = isSpotMarket
    ? Number(stats?.spot?.dailyAvgProfit) || 0
    : Number(stats?.futures?.dailyAvgProfit ?? stats.dailyAvgProfit) || 0;
  const avgTradePnl = isSpotMarket
    ? Number(stats?.spot?.pnl) || 0
    : Number(stats?.futures?.pnl ?? stats.pnl) || 0;
  const avgTradeRoi = isSpotMarket
    ? Number(stats?.spot?.avgTradeRoi) || 0
    : Number(stats?.futures?.avgTradeRoi ?? stats.avgTradeRoi) || 0;
  const winRate = Number(stats.winRate) || 0;

  useEffect(() => {
    let cancelled = false;

    const fetchSpotLiveTrades = async () => {
      if (selectedMarket !== "spot" || !user?._id) {
        setSpotTotalTrades(0);
        return;
      }

      try {
        const response = await api(
          "post",
          "livetrade/getLiveTrade?marketType=spot"
        );
        const openPositions = response?.data?.[0]?.openPositions || [];
        if (!cancelled) {
          setSpotTotalTrades(openPositions.length);
        }
      } catch (err) {
        if (!cancelled) setSpotTotalTrades(0);
      }
    };

    fetchSpotLiveTrades();
    return () => {
      cancelled = true;
    };
  }, [selectedMarket, user?._id]);

  const items = [
    {
      label: "Avg. Profit",
      value: `$ ${formatNumber(avgProfit)}`,
      tone: getToneClass(avgProfit),
    },
    {
      label: "Avg. Trade PNL",
      value: `${avgTradePnl > 0 ? "+" : ""}${formatNumber(avgTradePnl)} %`,
      tone: getToneClass(avgTradePnl),
    },
    {
      label: "Avg. Trade ROI",
      // Was "N/A" for spot as a stand-in while stats.spot.avgTradeRoi didn't
      // exist yet — now that the backend returns it, show the real value.
      value: `${formatNumber(avgTradeRoi)} %`,
      tone: getToneClass(avgTradeRoi),
    },
    {
      label: isSpotMarket ? "Total Trades" : "Win Rate",
      value: isSpotMarket
        ? `${spotTotalTrades}`
        : `${formatNumber(winRate)} %`,
      tone: isSpotMarket ? "positive" : getToneClass(winRate),
    },
  ];

  return (
    <div className="copyTradingStatsCard">
      <h3 className="copyTradingStatsCard__title">Copy Trading Statistics</h3>
      <div className="copyTradingStatsCard__grid">
        {items.map((item) => (
          <div
            key={item.label}
            className={`copyTradingStatsCard__item copyTradingStatsCard__item--${item.tone}`}
          >
            <p className="copyTradingStatsCard__label">{item.label}</p>
            <p className="copyTradingStatsCard__value">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CopyTradingStats;
